import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Trash2, Upload, ImageIcon, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { DietaryTagPicker } from './DietaryTags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Categories dropdown: pick an existing category or "+ Add new category"
 * (which expands an inline text input).
 */
export const CategorySelect = ({ value, onChange, options = [], testIdPrefix = 'menu-edit' }) => {
  const [adding, setAdding] = useState(false);
  const opts = Array.from(new Set(options.filter(Boolean)));
  const showSelect = !adding && opts.length > 0;

  if (adding) {
    return (
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="New category name"
          autoFocus
          className="bg-black/40 border-white/10 text-white h-10 rounded-xl flex-1"
          data-testid={`${testIdPrefix}-category-new-input`}
        />
        <Button
          onClick={() => setAdding(false)}
          variant="ghost"
          className="h-10 w-10 p-0 rounded-xl border border-white/10 text-gray-400 hover:text-white"
          data-testid={`${testIdPrefix}-category-new-cancel`}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  if (!showSelect) {
    // No options yet — just a text field
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Mains, Drinks, Curries…"
        className="bg-black/40 border-white/10 text-white h-10 rounded-xl"
        data-testid={`${testIdPrefix}-category`}
      />
    );
  }

  return (
    <select
      value={opts.includes(value) ? value : '__new__'}
      onChange={(e) => {
        if (e.target.value === '__new__') { onChange(''); setAdding(true); }
        else onChange(e.target.value);
      }}
      className="w-full bg-black/40 border border-white/10 text-white h-10 rounded-xl px-3 text-sm focus:outline-none focus:border-[#00FFFF]/40"
      data-testid={`${testIdPrefix}-category-select`}
    >
      {opts.map((o) => (
        <option key={o} value={o} className="bg-[#0a0a0a]">{o}</option>
      ))}
      <option value="__new__" className="bg-[#0a0a0a] text-[#00FFFF]">+ Add new category…</option>
    </select>
  );
};

/**
 * Variations editor: list of {name, price} rows with add/remove controls.
 * If at least one valid row exists, the guest menu hides the item's base price
 * and shows variations underneath instead.
 */
export const VariationsEditor = ({ value = [], onChange, testIdPrefix = 'menu-edit' }) => {
  const setRow = (idx, patch) => {
    const next = value.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const addRow = () => onChange([...(value || []), { name: '', price: 0 }]);
  const removeRow = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-variations`}>
      {value.length === 0 ? (
        <p className="text-xs text-gray-500">No variations. Use the base price above for a single-priced item.</p>
      ) : (
        value.map((row, idx) => (
          // Key must be STABLE per row — including `row.name` would cause React
          // to unmount/remount the row on every keystroke, killing focus and
          // making the cursor jump to the next field (price). Using the index
          // alone is correct here because we never reorder, only append / remove.
          <div key={`var-${idx}`} className="space-y-1.5" data-testid={`${testIdPrefix}-variation-${idx}`}>
            <div className="flex gap-2">
              <Input
                value={row.name || ''}
                onChange={(e) => setRow(idx, { name: e.target.value })}
                placeholder="Paneer / Chicken / Mutton…"
                className="bg-black/40 border-white/10 text-white h-9 rounded-lg flex-1"
                data-testid={`${testIdPrefix}-variation-name-${idx}`}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                value={row.price ?? 0}
                onChange={(e) => setRow(idx, { price: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                className="bg-black/40 border-white/10 text-white h-9 rounded-lg w-24 font-mono"
                data-testid={`${testIdPrefix}-variation-price-${idx}`}
              />
              <Button
                onClick={() => removeRow(idx)}
                variant="ghost"
                className="h-9 w-9 p-0 rounded-lg text-[#FF3B3B] hover:bg-[#FF3B3B]/10"
                title="Remove"
                data-testid={`${testIdPrefix}-variation-remove-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            {/* Per-variation dietary tags — "Biryani" can be Veg (paneer) OR
                Non-veg (chicken) on the SAME dish, so each variation carries
                its own tag set independent of the parent. */}
            <DietaryTagPicker
              value={row.dietary_tags || []}
              onChange={(tags) => setRow(idx, { dietary_tags: tags })}
              testIdPrefix={`${testIdPrefix}-variation-${idx}`}
            />
          </div>
        ))
      )}
      <Button
        onClick={addRow}
        variant="ghost"
        className="text-[#00FFFF] hover:bg-[#00FFFF]/10 h-8 px-3 rounded-full text-[11px] font-bold tracking-wider"
        data-testid={`${testIdPrefix}-variation-add`}
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> ADD VARIATION
      </Button>
    </div>
  );
};

/**
 * Image uploader: file input + remove button. Calls /api/uploads/menu-image
 * and sets the returned photo_url on the parent draft.
 */
export const ImageUploader = ({ value, onChange, testIdPrefix = 'menu-edit' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Resolve relative photo_url against the backend host so the <img> works.
  const resolved = value
    ? (value.startsWith('http') ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`)
    : '';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/uploads/menu-image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.photo_url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-image-uploader`}>
      <div className="w-16 h-16 rounded-xl bg-black/40 border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0">
        {resolved ? (
          <img src={resolved} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-5 h-5 text-gray-600" />
        )}
      </div>
      <div className="flex-1 flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={handleFile}
          data-testid={`${testIdPrefix}-image-file-input`}
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          variant="ghost"
          className="justify-start border border-white/10 rounded-xl h-9 text-xs text-gray-200 hover:bg-white/5 font-semibold tracking-wider"
          data-testid={`${testIdPrefix}-image-upload-btn`}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-2" />}
          {value ? 'REPLACE IMAGE' : 'UPLOAD IMAGE'}
        </Button>
        {value && (
          <button
            onClick={() => onChange('')}
            className="text-[10px] text-[#FF3B3B] hover:underline text-left"
            data-testid={`${testIdPrefix}-image-remove`}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};
